/**
 * Hub Routes — single source of truth for the 8-hub IA.
 *
 * Each hub has a path and a list of legacy routes that should be
 * redirected to it, optionally with a `?section=` query param that
 * the hub shell uses to open the right tab.
 *
 * The `id` matches a `section` value that hub pages understand; hub
 * shells read `?section=` from the URL on mount and activate the
 * corresponding tab.
 *
 * This file is consumed by:
 *   - `App.tsx` (legacy redirect routes)
 *   - `IUILayout.tsx` (first-run target)
 *   - `UnifiedSidebar.tsx` (nav)
 *   - `CommandPalette.tsx` (skill → hub mapping)
 */

export type HubId =
  | 'home'
  | 'tabs'
  | 'ai'
  | 'apps'
  | 'productivity'
  | 'insights'
  | 'sync'
  | 'settings';

export interface HubDef {
  id: HubId;
  path: string;
  title: string;
  /** Lucide icon name (we import the actual icon in components). */
  iconName:
    | 'LayoutDashboard'
    | 'Monitor'
    | 'Brain'
    | 'Sparkles'
    | 'Target'
    | 'BarChart3'
    | 'Cloud'
    | 'Settings';
}

export const HUBS: HubDef[] = [
  { id: 'home', path: '/home', title: 'Home', iconName: 'LayoutDashboard' },
  { id: 'tabs', path: '/tabs', title: 'Tabs', iconName: 'Monitor' },
  { id: 'ai', path: '/ai', title: 'AI', iconName: 'Brain' },
  { id: 'apps', path: '/apps', title: 'Apps', iconName: 'Sparkles' },
  { id: 'productivity', path: '/productivity', title: 'Productivity', iconName: 'Target' },
  { id: 'insights', path: '/insights', title: 'Insights', iconName: 'BarChart3' },
  { id: 'sync', path: '/sync', title: 'Sync & Share', iconName: 'Cloud' },
  { id: 'settings', path: '/settings', title: 'Settings', iconName: 'Settings' },
];

/**
 * Map a legacy route to a hub destination.
 * `section` is the value the hub shell will read from `?section=`.
 * `sub` is an optional sub-route segment (e.g. `/apps/generate`).
 */
export interface HubRedirect {
  from: string;
  to: string; // hub path or sub-route path
  section?: string;
  /** Additional query params merged with section (e.g. view=list). */
  query?: Record<string, string>;
}

export const HUB_REDIRECTS: HubRedirect[] = [
  // -------- Home hub --------
  { from: '/dashboard', to: '/home' },
  { from: '/suggestions', to: '/home', section: 'suggestions' },
  { from: '/activity', to: '/home', section: 'activity' },
  { from: '/activity-stream', to: '/home', section: 'activity' },
  { from: '/insights', to: '/home', section: 'insights' },
  { from: '/meeting-prep', to: '/home', section: 'meeting' },

  // -------- Tabs hub --------
  { from: '/tabs', to: '/tabs', section: 'live' },
  { from: '/monitor', to: '/tabs', section: 'live' },
  { from: '/monitor/groups', to: '/tabs', section: 'live', query: { view: 'clusters' } },
  { from: '/monitor/security', to: '/tabs', section: 'live', query: { view: 'list' } },
  { from: '/monitor/search', to: '/tabs', section: 'live' },
  { from: '/monitor/collaboration', to: '/tabs', section: 'collaboration' },
  { from: '/tab-groups', to: '/tabs', section: 'live', query: { view: 'clusters' } },
  { from: '/smart-groups', to: '/tabs', section: 'live', query: { view: 'clusters' } },
  { from: '/visual-tab-organization', to: '/tabs', section: 'live', query: { view: 'clusters' } },
  { from: '/tab-security', to: '/tabs', section: 'live', query: { view: 'list' } },
  { from: '/tab-search', to: '/tabs', section: 'live' },
  { from: '/tab-collaboration', to: '/tabs', section: 'collaboration' },
  { from: '/tab-memory', to: '/tabs', section: 'memory' },
  { from: '/tab-time-machine', to: '/tabs', section: 'time-machine' },
  { from: '/tab-timeline-viewer', to: '/tabs', section: 'time-machine' },
  { from: '/tab-sleep', to: '/tabs', section: 'live' },
  { from: '/tab-preview', to: '/tabs', section: 'preview' },
  { from: '/tab-version-control', to: '/tabs', section: 'time-machine' },
  { from: '/tab-version-history', to: '/tabs', section: 'time-machine' },
  { from: '/version-history', to: '/tabs', section: 'time-machine' },
  { from: '/tab-recycling', to: '/tabs', section: 'recycle' },
  { from: '/tab-handoff', to: '/tabs', section: 'handoff' },
  { from: '/tab-notifications', to: '/tabs', section: 'live' },
  { from: '/tab-timeline', to: '/tabs', section: 'time-machine' },
  { from: '/tab-workflows', to: '/tabs/workflows' },
  { from: '/tab-intelligence', to: '/ai', section: 'tab-intel' },
  { from: '/proactive-intelligence', to: '/ai', section: 'tab-intel' },
  { from: '/project-colors', to: '/tabs', section: 'colors' },
  { from: '/voice-command', to: '/tabs', section: 'voice' },
  { from: '/stash', to: '/tabs', section: 'live' },
  { from: '/tab-prediction', to: '/tabs', section: 'predict' },

  // -------- AI hub --------
  { from: '/ai', to: '/ai', section: 'chat' },
  { from: '/contextual-intelligence', to: '/ai', section: 'contextual' },
  { from: '/session-intelligence', to: '/ai', section: 'session' },
  { from: '/visual-intelligence', to: '/ai', section: 'patterns' },
  { from: '/pattern-detection', to: '/ai', section: 'patterns' },
  { from: '/smart-actions', to: '/ai', section: 'smart-actions' },
  { from: '/relationship-map', to: '/ai', section: 'relationships' },
  { from: '/entity-extraction', to: '/ai', section: 'entities' },
  { from: '/context-aggregator', to: '/ai', section: 'contextual' },
  { from: '/workflow-detection', to: '/ai', section: 'smart-actions' },

  // -------- Apps hub --------
  { from: '/generate', to: '/apps', section: 'generate' },
  { from: '/apps/generate', to: '/apps', section: 'generate' },
  { from: '/apps', to: '/apps' },
  { from: '/compare', to: '/apps/compare' },
  { from: '/timeline', to: '/apps/timeline' },
  { from: '/synthesize', to: '/apps/synthesize' },
  { from: '/code-snippets', to: '/apps', section: 'developer' },
  { from: '/browser-automation', to: '/apps', section: 'developer' },
  { from: '/webhook-management', to: '/apps', section: 'developer' },
  { from: '/api-tester', to: '/apps', section: 'developer' },
  { from: '/templates', to: '/apps', section: 'templates' },

  // -------- Productivity hub --------
  { from: '/focus', to: '/productivity', section: 'focus' },
  { from: '/pomodoro', to: '/productivity', section: 'pomodoro' },
  { from: '/time-tracking', to: '/productivity', section: 'time' },
  { from: '/goals', to: '/productivity', section: 'goals' },
  { from: '/habit-tracking', to: '/productivity', section: 'habits' },
  { from: '/distraction-blocking', to: '/productivity', section: 'focus' },
  { from: '/drag-drop', to: '/productivity', section: 'drag' },

  // -------- Insights hub --------
  { from: '/analytics', to: '/insights', section: 'overview' },
  { from: '/enhanced-analytics', to: '/insights', section: 'overview' },
  { from: '/attribution-tracker', to: '/insights', section: 'data' },
  { from: '/citation-tracking', to: '/insights', section: 'content' },
  { from: '/content-extraction', to: '/insights', section: 'content' },
  { from: '/content-analysis', to: '/insights', section: 'content' },
  { from: '/advanced-search', to: '/insights', section: 'overview' },
  { from: '/data-aggregation', to: '/apps/data-aggregation' },
  { from: '/export-center', to: '/insights', section: 'export' },
  { from: '/backup-center', to: '/insights', section: 'backup' },
  { from: '/import-center', to: '/insights', section: 'backup' },

  // -------- Sync hub --------
  { from: '/cross-platform-sync', to: '/sync', section: 'sync' },
  { from: '/realtime-sync', to: '/sync', section: 'realtime' },
  { from: '/collaborative-session', to: '/sync', section: 'collab' },
  { from: '/mesh-network', to: '/sync', section: 'mesh' },
  { from: '/mobile-companion', to: '/sync', section: 'mobile' },
  { from: '/mobile-share', to: '/sync', section: 'share' },
  { from: '/sync-share', to: '/sync', section: 'sync' },
  { from: '/pwa-settings', to: '/sync', section: 'pwa' },
  { from: '/offline-mode', to: '/sync', section: 'offline' },

  // -------- Settings hub --------
  { from: '/settings', to: '/settings' },
  { from: '/ai-providers', to: '/settings', section: 'ai-providers' },
  { from: '/keyboard-shortcuts', to: '/settings', section: 'shortcuts' },
  { from: '/admin/users', to: '/settings/admin/users' },
  { from: '/enterprise', to: '/settings', section: 'enterprise' },
  { from: '/sessions', to: '/settings', section: 'sessions' },
  { from: '/bookmarks', to: '/settings', section: 'sessions' },
  { from: '/comments', to: '/settings', section: 'sessions' },
  { from: '/skills', to: '/settings/skills' },
  { from: '/agentic-orchestration', to: '/settings/agentic' },
];

/**
 * Build a URL string from a HubRedirect, with section param if present.
 */
export function redirectTarget(redirect: HubRedirect): string {
  const params = new URLSearchParams();
  if (redirect.section) {
    params.set('section', redirect.section);
  }
  if (redirect.query) {
    for (const [key, value] of Object.entries(redirect.query)) {
      params.set(key, value);
    }
  }
  if (params.toString()) {
    const sep = redirect.to.includes('?') ? '&' : '?';
    return `${redirect.to}${sep}${params.toString()}`;
  }
  return redirect.to;
}
